// API wird später implementiert - für jetzt verwenden wir Mock-Daten

// ---- Runtime configuration (via Vite env) ----
const RAW_API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000';
const USE_MOCK = ((import.meta as any).env?.VITE_USE_MOCK_SCREENER || 'false') === 'true';
const WITH_CREDENTIALS = ((import.meta as any).env?.VITE_WITH_CREDENTIALS || 'false') === 'true';
// Primary screener path (nutze Legacy als Fallback)
const SCREENER_PRIMARY_PATH = (import.meta as any).env?.VITE_SCREENER_PATH || '/api/screener';
const SCREENER_LEGACY_PATH = '/api/screener/search';

// Seit 13.09.2026 verlangt der Kennzahlen-Screener eine Anmeldung.
function anmeldeKopf(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


export interface ScreenerFilters {
  market_cap_min?: number;
  market_cap_max?: number;
  pe_ratio_max?: number;
  peg_ratio_max?: number;
  fcf_yield_min?: number;
  revenue_growth_min?: number;
  gross_margin_min?: number;
  dividend_yield_min?: number;
  sector?: string;
  country?: string;
  debt_to_equity_max?: number;
  earnings_growth_min?: number;
}

export interface ScreenerRequest {
  filters: ScreenerFilters;
  sort_by: string;
  sort_order: 'asc' | 'desc';
  page: number;
  page_size: number;
}

export interface ScreenerResult {
  symbol: string;
  name: string;
  price?: number;
  market_cap?: number;
  pe_ratio?: number;
  peg_ratio?: number;
  fcf_yield?: number;
  revenue_growth?: number;
  gross_margin?: number;
  dividend_yield?: number;
  sector?: string;
  country?: string;
  qualyz_score?: number;
}

export interface ScreenerResponse {
  results: ScreenerResult[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ScreenerPreset {
  name: string;
  description: string;
  filters: ScreenerFilters;
}

// Standard-Presets
export const screenerPresets: Record<string, ScreenerPreset> = {
  qualyz: {
    name: 'Qualyz',
    description: 'Unsere Premium-Strategie mit 5 Kriterien',
    filters: {
      peg_ratio_max: 2.0,
      fcf_yield_min: 5.0,
      market_cap_min: 100000000,
      revenue_growth_min: 5.0,
      gross_margin_min: 40.0
    }
  },
  graham: {
    name: 'Graham/Value',
    description: 'Klassische Value-Investing-Strategie',
    filters: {
      pe_ratio_max: 15.0,
      peg_ratio_max: 1.5,
      debt_to_equity_max: 0.5
    }
  },
  peterLynch: {
    name: 'Peter Lynch',
    description: 'PEG ≈ 1.0 Strategie',
    filters: {
      peg_ratio_max: 1.2,
      revenue_growth_min: 10.0
    }
  },
  strongGrowth: {
    name: 'Strong Growth',
    description: 'Fokus auf hohes Wachstum',
    filters: {
      revenue_growth_min: 20.0,
      earnings_growth_min: 15.0
    }
  }
};

class ScreenerService {
  /**
   * Aktien nach Kriterien screenen
   */
  async screenStocks(request: ScreenerRequest): Promise<ScreenerResponse> {
    // Fallback hart auf Mock, wenn explizit gewünscht
    if (USE_MOCK) {
      return this.getMockScreenerResponse(request);
    }

    // Convert camelCase filters to snake_case for backend
    const convertedFilters: any = {};
    if (request.filters) {
      Object.entries(request.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert camelCase to snake_case
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          convertedFilters[snakeKey] = value;
        }
      });
    }

    // Payload so bauen, dass beide Conventions (snake/camel) abgedeckt sind
    const payload: any = {
      // snake_case (für Backend)
      filters: convertedFilters,
      sort_by: request.sort_by,
      sort_order: request.sort_order,
      page: request.page,
      page_size: request.page_size,
    };

    const fetchOpts: RequestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...anmeldeKopf() },
      body: JSON.stringify(payload),
    };
    if (WITH_CREDENTIALS) {
      fetchOpts.credentials = 'include';
    }

    // Zuerst neuer Pfad `/api/screener/search`; bei 404 auf Legacy `/api/screener` zurückfallen
    const apiBase = RAW_API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');
    const primaryUrl = `${apiBase}${SCREENER_PRIMARY_PATH}`;
    const legacyUrl = `${apiBase}${SCREENER_LEGACY_PATH}`;

    try {
      console.log('[Screener] POST', primaryUrl, payload);
      let res = await fetch(primaryUrl, fetchOpts);

      if (res.status === 404) {
        console.warn('[Screener] Primary endpoint 404, falling back to legacy path:', legacyUrl);
        res = await fetch(legacyUrl, fetchOpts);
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`[Screener] Backend error ${res.status} ${res.statusText}: ${text}`);
      }

      const data = await res.json();
      console.log('[Screener] Response OK:', data);
      return data;
    } catch (err) {
      console.warn('[Screener] Backend nicht verfügbar oder Fehler:', err);
      throw err;
    }
  }

  /**
   * Alle verfügbaren Presets abrufen
   */
  async getPresets(): Promise<Record<string, ScreenerPreset>> {
    // Für jetzt verwenden wir lokale Presets
    return screenerPresets;
  }

  /**
   * Einzelnes Preset abrufen
   */
  async getPreset(presetKey: string): Promise<ScreenerPreset | null> {
    // Für jetzt verwenden wir lokale Presets
    return screenerPresets[presetKey] || null;
  }

  /**
   * Verfügbare Sektoren vom Backend abrufen
   */
  async getSectors(): Promise<string[]> {
    try {
      const apiBase = RAW_API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/api/screener/sectors`, { credentials: WITH_CREDENTIALS ? 'include' : 'same-origin', headers: anmeldeKopf() });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.warn('Failed to fetch sectors:', error);
    }
    // Fallback: Standard-Sektoren
    return ['Technology', 'Healthcare', 'Financial', 'Consumer', 'Industrial', 'Energy', 'Utilities', 'Materials', 'Real Estate', 'Communication'];
  }

  /**
   * Verfügbare Länder vom Backend abrufen
   */
  async getCountries(): Promise<string[]> {
    try {
      const apiBase = RAW_API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/api/screener/countries`, { credentials: WITH_CREDENTIALS ? 'include' : 'same-origin', headers: anmeldeKopf() });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.warn('Failed to fetch countries:', error);
    }
    // Fallback: Standard-Länder
    return ['USA', 'Germany', 'UK', 'France', 'Japan', 'Canada', 'Australia', 'Switzerland', 'Netherlands', 'Sweden'];
  }

  /**
   * Mock-Daten für Entwicklung (wird später durch echte API-Daten ersetzt)
   */
  getMockData(): ScreenerResult[] {
    return [
      {
        symbol: 'AAPL',
        name: 'Apple Inc.',
        price: 150.25,
        market_cap: 2500000000000,
        pe_ratio: 25.5,
        peg_ratio: 1.8,
        fcf_yield: 6.2,
        revenue_growth: 8.5,
        gross_margin: 42.5,
        dividend_yield: 0.6,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 85
      },
      {
        symbol: 'MSFT',
        name: 'Microsoft Corporation',
        price: 320.75,
        market_cap: 2400000000000,
        pe_ratio: 28.2,
        peg_ratio: 1.9,
        fcf_yield: 5.8,
        revenue_growth: 12.3,
        gross_margin: 68.9,
        dividend_yield: 0.8,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 92
      },
      {
        symbol: 'GOOGL',
        name: 'Alphabet Inc.',
        price: 2750.50,
        market_cap: 1850000000000,
        pe_ratio: 22.1,
        peg_ratio: 1.6,
        fcf_yield: 7.1,
        revenue_growth: 15.7,
        gross_margin: 55.2,
        dividend_yield: 0.0,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 88
      },
      {
        symbol: 'AMZN',
        name: 'Amazon.com Inc.',
        price: 3200.00,
        market_cap: 1650000000000,
        pe_ratio: 45.8,
        peg_ratio: 2.1,
        fcf_yield: 3.2,
        revenue_growth: 22.4,
        gross_margin: 38.7,
        dividend_yield: 0.0,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 72
      },
      {
        symbol: 'TSLA',
        name: 'Tesla Inc.',
        price: 850.25,
        market_cap: 850000000000,
        pe_ratio: 125.6,
        peg_ratio: 3.2,
        fcf_yield: 1.8,
        revenue_growth: 45.2,
        gross_margin: 25.4,
        dividend_yield: 0.0,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 45
      },
      {
        symbol: 'NVDA',
        name: 'NVIDIA Corporation',
        price: 450.75,
        market_cap: 1100000000000,
        pe_ratio: 35.2,
        peg_ratio: 1.4,
        fcf_yield: 8.9,
        revenue_growth: 28.7,
        gross_margin: 62.3,
        dividend_yield: 0.2,
        sector: 'Technology',
        country: 'USA',
        qualyz_score: 95
      },
      {
        symbol: 'JNJ',
        name: 'Johnson & Johnson',
        price: 165.50,
        market_cap: 430000000000,
        pe_ratio: 18.7,
        peg_ratio: 1.2,
        fcf_yield: 4.8,
        revenue_growth: 6.8,
        gross_margin: 67.2,
        dividend_yield: 2.8,
        sector: 'Healthcare',
        country: 'USA',
        qualyz_score: 78
      },
      {
        symbol: 'JPM',
        name: 'JPMorgan Chase & Co.',
        price: 145.25,
        market_cap: 440000000000,
        pe_ratio: 12.3,
        peg_ratio: 0.9,
        fcf_yield: 6.5,
        revenue_growth: 4.2,
        gross_margin: 85.6,
        dividend_yield: 3.2,
        sector: 'Financial',
        country: 'USA',
        qualyz_score: 82
      },
      {
        symbol: 'V',
        name: 'Visa Inc.',
        price: 245.75,
        market_cap: 520000000000,
        pe_ratio: 32.8,
        peg_ratio: 1.7,
        fcf_yield: 5.2,
        revenue_growth: 11.4,
        gross_margin: 78.9,
        dividend_yield: 0.8,
        sector: 'Financial',
        country: 'USA',
        qualyz_score: 87
      },
      {
        symbol: 'PG',
        name: 'Procter & Gamble Co.',
        price: 145.50,
        market_cap: 350000000000,
        pe_ratio: 24.6,
        peg_ratio: 1.8,
        fcf_yield: 4.2,
        revenue_growth: 3.8,
        gross_margin: 52.4,
        dividend_yield: 2.9,
        sector: 'Consumer',
        country: 'USA',
        qualyz_score: 75
      }
    ];
  }

  /**
   * Mock-Screener-Response für Entwicklung
   */
  async getMockScreenerResponse(request: ScreenerRequest): Promise<ScreenerResponse> {
    // Simuliere API-Verzögerung
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let results = this.getMockData();
    
    // Filter anwenden
    if (request.filters.market_cap_min) {
      results = results.filter(stock => stock.market_cap && stock.market_cap >= request.filters.market_cap_min!);
    }
    
    if (request.filters.market_cap_max) {
      results = results.filter(stock => stock.market_cap && stock.market_cap <= request.filters.market_cap_max!);
    }
    
    if (request.filters.pe_ratio_max) {
      results = results.filter(stock => stock.pe_ratio && stock.pe_ratio <= request.filters.pe_ratio_max!);
    }
    
    if (request.filters.peg_ratio_max) {
      results = results.filter(stock => stock.peg_ratio && stock.peg_ratio <= request.filters.peg_ratio_max!);
    }
    
    if (request.filters.fcf_yield_min) {
      results = results.filter(stock => stock.fcf_yield && stock.fcf_yield >= request.filters.fcf_yield_min!);
    }
    
    if (request.filters.revenue_growth_min) {
      results = results.filter(stock => stock.revenue_growth && stock.revenue_growth >= request.filters.revenue_growth_min!);
    }
    
    if (request.filters.gross_margin_min) {
      results = results.filter(stock => stock.gross_margin && stock.gross_margin >= request.filters.gross_margin_min!);
    }
    
    if (request.filters.dividend_yield_min) {
      results = results.filter(stock => stock.dividend_yield && stock.dividend_yield >= request.filters.dividend_yield_min!);
    }
    
    if (request.filters.sector) {
      results = results.filter(stock => stock.sector === request.filters.sector);
    }
    
    if (request.filters.country) {
      results = results.filter(stock => stock.country === request.filters.country);
    }
    
    // Sortierung anwenden
    results.sort((a, b) => {
      let aValue: any = a[request.sort_by as keyof ScreenerResult];
      let bValue: any = b[request.sort_by as keyof ScreenerResult];
      
      if (aValue === undefined || aValue === null) aValue = 0;
      if (bValue === undefined || bValue === null) bValue = 0;
      
      if (request.sort_order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    // Paginierung anwenden
    const startIndex = (request.page - 1) * request.page_size;
    const endIndex = startIndex + request.page_size;
    const paginatedResults = results.slice(startIndex, endIndex);
    
    return {
      results: paginatedResults,
      total: results.length,
      page: request.page,
      page_size: request.page_size,
      total_pages: Math.ceil(results.length / request.page_size)
    };
  }
}

export const screenerService = new ScreenerService();
